// One row in the redesigned Wilman tab. Three render variants
// driven by the `kind` prop:
//
//   open       — full transmission card + inline ChallengeSubmit
//                (expand-on-tap so the list stays scrollable)
//   completed  — muted header + audio + verdict line + points
//   missed     — title + audio + read-only timestamp
//
// Reuses TransmissionCard for the open variant's full body so the
// existing copper border + voice player + bonus list render identically
// to the single-active card players already know.

import { useState } from 'react';
import TransmissionCard from './TransmissionCard.jsx';
import ChallengeSubmit from './ChallengeSubmit.jsx';
import VoicePlayer from './VoicePlayer.jsx';
import { cleanWilmanText } from '../lib/wilmanText.js';

function relativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  if (ms < 60_000) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function BacklogCard({
  entry,
  kind,
  tripId,
  playerId,
  onVerified,
  onDismiss,
  dismissing,
}) {
  if (kind === 'open') return <OpenCard entry={entry} tripId={tripId} playerId={playerId} onVerified={onVerified} onDismiss={onDismiss} dismissing={dismissing} />;
  if (kind === 'completed') return <CompletedCard entry={entry} />;
  return <MissedCard entry={entry} />;
}

function OpenCard({ entry, tripId, playerId, onVerified, onDismiss, dismissing }) {
  const [expanded, setExpanded] = useState(false);
  const { transmission } = entry;

  if (!expanded) {
    return (
      <li>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="card flex w-full items-start justify-between gap-3 border-l-4 border-l-copper px-4 py-3 text-left transition hover:bg-bg-tertiary"
        >
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              incoming · {relativeTime(transmission.fired_at)}
            </p>
            <p className="mt-1 truncate font-medium text-fg-primary">
              {transmission.challenge_title}
            </p>
            <p className="mt-1 line-clamp-2 font-serif text-sm italic text-fg-secondary">
              {cleanWilmanText(transmission.challenge_text)}
            </p>
          </div>
          <span aria-hidden className="shrink-0 self-center font-mono text-[10px] uppercase tracking-wider text-action">
            open ›
          </span>
        </button>
      </li>
    );
  }

  return (
    <li className="space-y-2">
      <TransmissionCard transmission={transmission}>
        <ChallengeSubmit
          transmission={transmission}
          tripId={tripId}
          playerId={playerId}
          onVerified={(payload) => {
            setExpanded(false);
            onVerified?.(payload);
          }}
        />
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary transition hover:bg-bg-tertiary"
          >
            collapse
          </button>
          {onDismiss && (
            <button
              type="button"
              onClick={() => onDismiss(transmission)}
              disabled={dismissing}
              className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary transition hover:bg-bg-tertiary disabled:opacity-50"
            >
              move on
            </button>
          )}
        </div>
      </TransmissionCard>
    </li>
  );
}

function CompletedCard({ entry }) {
  const { transmission, submission } = entry;
  const verified = !!submission?.verified;
  // Confidence is a 0..1 number from the verifier. Show on rejected
  // cards so the player understands the verdict was data-grounded
  // (e.g., "confidence 0.05" reads as "obvious mismatch") rather than
  // arbitrary. Suppressed on verified rows per Item 3 scope.
  const confidence =
    typeof submission?.confidence === 'number' ? submission.confidence : null;
  // photo_url is server-signed via signSubmissionsList; photo_urls
  // is the multi-photo variant (e.g., canoe-trio submissions).
  const photoUrl = submission?.photo_url;
  const photoUrls = Array.isArray(submission?.photo_urls) ? submission.photo_urls.filter(Boolean) : [];
  const hasMulti = photoUrls.length > 1;
  return (
    <li className="card overflow-hidden opacity-80">
      <header className="flex items-baseline justify-between gap-2 border-b border-border-tertiary bg-bg-secondary px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        <span>{transmission.challenge_title}</span>
        <span>{relativeTime(submission?.submitted_at || transmission.fired_at)}</span>
      </header>
      {hasMulti ? (
        <ul className="grid grid-cols-3 gap-px bg-border-tertiary">
          {photoUrls.slice(0, 3).map((u, i) => (
            <li key={i} className="aspect-square bg-black">
              <img
                src={u}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            </li>
          ))}
        </ul>
      ) : photoUrl ? (
        <div className="bg-black">
          <img
            src={photoUrl}
            alt=""
            className="h-44 w-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        </div>
      ) : null}
      <div className="space-y-2 px-4 py-3">
        {transmission.audio_url && (
          <VoicePlayer
            url={transmission.audio_url}
            duration={transmission.audio_duration}
            label="replay"
          />
        )}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={[
              'rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider',
              verified
                ? 'border-success/40 bg-success/10 text-success'
                : 'border-danger/40 bg-danger/10 text-danger',
            ].join(' ')}
          >
            {verified ? 'verified' : 'rejected'}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {submission?.points ?? 0} pts
          </span>
          {!verified && confidence != null && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              confidence {confidence.toFixed(2)}
            </span>
          )}
        </div>
        {submission?.wilman_commentary && (
          <p className="font-serif italic text-sm text-fg-secondary">
            &ldquo;{submission.wilman_commentary}&rdquo;
          </p>
        )}
      </div>
    </li>
  );
}

function MissedCard({ entry }) {
  const { transmission } = entry;
  return (
    <li className="card overflow-hidden opacity-70">
      <header className="flex items-baseline justify-between gap-2 border-b border-border-tertiary bg-bg-secondary px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        <span>{transmission.challenge_title}</span>
        <span>missed · {relativeTime(transmission.fired_at)}</span>
      </header>
      <div className="space-y-2 px-4 py-3">
        {transmission.audio_url && (
          <VoicePlayer
            url={transmission.audio_url}
            duration={transmission.audio_duration}
            label="replay"
          />
        )}
        <p className="line-clamp-3 font-serif text-sm italic text-fg-secondary">
          {cleanWilmanText(transmission.challenge_text)}
        </p>
      </div>
    </li>
  );
}
